import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { charleyMemoryConfig } from './config';
import bgImage from './assets/bg.png';
import photoIluvu20260213 from './assets/iluvu-20260213.png';
import { walkSongLyrics } from './walk-song-lyrics';
import { decryptContent } from './utils';
import { CharleyMemoryApiError, CharleyMemoryApiErrorCode, loginAndFetchRecords, trackEvent } from './api';
import { EVENTS } from './events';
import { EnvelopeGate } from './EnvelopeGate';
import { DateGraph } from './DateGraph';
import { GalleryModal } from './GalleryModal';
import { MemoryCard } from './MemoryCard';
import { LyricsOverlay } from './LyricsOverlay';
import { CharleyMemoryMapView } from './CharleyMemoryMapView';
import { CharleyMemoryStatsView } from './CharleyMemoryStatsView';
import { CharleyMemoryGuestbookView } from './Guestbook';
import spots from './spots.json';

const CM_PASSWORD_KEY = 'charley_memory_pwd';
// Display version: X.Y.Z-MMDDHHmm — single semver-pre-release string read
// straight from package.json.version by `scripts/print-display-version.js`
// at build time and injected as REACT_APP_VERSION. Stable across rebuilds
// of the same commit; only changes when `npm run bump` runs. Falls back
// to 'dev' under `npm start` (no build-time injection there).
const CM_VERSION = process.env.REACT_APP_VERSION || 'dev';

const VersionBadge = styled.span`
  position: fixed;
  bottom: 10px;
  right: 12px;
  font-size: 11px;
  color: rgba(255,255,255,0.25);
  pointer-events: none;
  z-index: 10;
  user-select: none;
`;

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* 手機動態 viewport，避免 iOS 工具列影響 */
  ${(p) =>
    p.$fillViewport &&
    'height: 100vh; height: 100dvh; overflow: hidden; box-sizing: border-box;'}
  padding: 100px 20px 80px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 60px 12px 40px;
  }
  @media (max-width: 480px) {
    padding: 20px 10px 32px;
  }
  background-image:
    linear-gradient(180deg, rgba(73, 79, 95, 0.72) 0%, rgba(7, 10, 18, 0.88) 100%),
    url(${bgImage});
  background-size: auto, cover;
  background-repeat: no-repeat, no-repeat;
  background-attachment: scroll, scroll;
  background-position: 0 0, 50% 100%;
  color: #e9ecf1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: ${(p) => (p.$fillViewport ? 'hidden' : 'visible')};
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: visible;
  ${(p) => p.$fillViewport && 'height: 100%; display: flex; flex-direction: column; min-height: 0;'}
`;

const AuthContentColumn = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 24px 32px;
  margin-bottom: 24px;
  @media (max-width: 768px) {
    gap: 16px 20px;
    margin-bottom: 16px;
  }
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 12px;
  }
`;

const Header = styled.div`
  flex-shrink: 0;
`;

const SubtitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  padding-left: 4px;
  @media (max-width: 480px) {
    gap: 10px 16px;
  }
`;

const ViewBtnGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 2px;
`;

/* 打開的信封袋：米色紙、上蓋折頁，卡片疊在裡面 */
const EnvelopePocket = styled.div`
  --scroll-gap-top: 22px;
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 680px;
  margin: var(--scroll-gap-top) auto 0;
  box-sizing: border-box;
  aspect-ratio: 4 / 3;
  min-height: 280px;
  background:
    linear-gradient(to bottom, transparent 88px, rgba(0, 0, 0, 0.06) 90px, transparent 92px),
    linear-gradient(145deg, #f5f0e8 0%, #e8e0d4 48%, #ddd4c8 100%);
  border: 1px solid rgba(120, 100, 80, 0.35);
  border-radius: 6px;
  box-shadow:
    inset 0 2px 4px rgba(255, 255, 255, 0.5),
    0 6px 20px rgba(0, 0, 0, 0.25),
    0 1px 0 rgba(255, 255, 255, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 32px 56px;
  overflow: visible;
  @media (max-width: 768px) {
    --scroll-gap-top: 18px;
    padding: 28px 20px 36px;
  }
  @media (max-width: 480px) {
    --scroll-gap-top: 14px;
    padding: 20px 12px 24px;
    min-height: 220px;
  }

  /* 信封上蓋折頁（打開後折下來的三角） */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 90px;
    background: linear-gradient(180deg, rgba(235, 228, 218, 0.98) 0%, rgba(224, 216, 204, 0.95) 70%, rgba(218, 208, 196, 0.92) 100%);
    clip-path: polygon(50% 100%, 0 0, 100% 0);
    pointer-events: none;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -2px 4px rgba(0, 0, 0, 0.06);
  }
`;

const ScrollDriver = styled.div`
  position: absolute;
  top: calc(0px - var(--scroll-gap-top, 0px));
  left: -32px;
  right: -32px;
  bottom: -56px;
  overflow: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  scrollbar-width: none;
  -ms-overflow-style: none;
  overscroll-behavior: contain;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 768px) {
    left: -20px;
    right: -20px;
    bottom: -36px;
  }
  @media (max-width: 480px) {
    left: -12px;
    right: -12px;
    bottom: -24px;
  }
`;

const ScrollDriverInner = styled.div`
  width: 1px;
  height: ${(p) => (p.$height != null ? `${p.$height}px` : '0')};
  pointer-events: none;
`;

const CardsStack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(233, 236, 241, 0.55);
  font-size: 0.9rem;
  text-align: center;
  pointer-events: auto;
  padding: 24px 16px;
`;

/* Header 用：標籤篩選、計數 */
const Tag = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f3efe6;
  border: 1px solid #e6dece;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.85rem;
  color: #3f3a33;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #e9ecf1;
  font-weight: 700;
  font-size: 0.95rem;
`;

const MapViewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: none;
  color: #e9ecf1;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.85;
  }
`;

const MapPinsStack = styled.span`
  position: relative;
  display: inline-block;
  width: 22px;
  height: 26px;
`;

const MapPinIcon = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  width: 18px;
  height: 22px;
  fill: currentColor;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.25));
  &:nth-of-type(1) {
    transform: translate(0, 0) rotate(-8deg);
    opacity: 0.5;
  }
  &:nth-of-type(2) {
    transform: translate(4px, 2px) rotate(4deg);
    opacity: 0.8;
  }
  &:nth-of-type(3) {
    transform: translate(8px, 4px) rotate(-2deg);
    opacity: 1;
  }
`;

const PIN_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z';

const ENCRYPTED_CONTENT_URL = `${process.env.PUBLIC_URL || ''}/charley-memory/content.encrypted`;

// 切換是否使用後端驗證與同步紀錄；目前改為純前端（static）驗證與載入內容
const USE_SERVER_AUTH = false;

const CharleyMemoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [decryptedContent, setDecryptedContent] = useState(null); // null = gate, array = unlocked
  const [openingPhase, setOpeningPhase] = useState(null); // 'seal-fall' | 'envelope-open'
  const [error, setError] = useState('');
  const [, setChecking] = useState(false);
  const formRef = useRef(null);
  const encryptedBlobRef = useRef(null);
  const isAuthed = decryptedContent !== null;
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [galleryEntry, setGalleryEntry] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const galleryScrollRef = useRef(null);
  const galleryIndexRef = useRef(0);
  const scrollDriverRef = useRef(null);
  const envelopeRef = useRef(null);
  const walkSongRef = useRef(null);
  const [vinylPlaying, setVinylPlaying] = useState(false);
  const [walkSongCurrentTime, setWalkSongCurrentTime] = useState(0);
  const [flownLyrics, setFlownLyrics] = useState([]);
  const prevLyricKeyRef = useRef(null);
  const [viewMode, setViewMode] = useState('content'); // 'content' | 'map' | 'stats'
  const navPassword = typeof location.state?.cm_password === 'string' ? location.state.cm_password : '';
  const usedNavPasswordRef = useRef(false);

  useEffect(() => {
    trackEvent(EVENTS.PAGE_VIEW, { path: window.location.pathname });
    fetch(ENCRYPTED_CONTENT_URL).then((res) => {
      if (res.ok) return res.arrayBuffer();
    }).then((buf) => { if (buf) encryptedBlobRef.current = buf; }).catch(() => {});
  }, []);

  useEffect(() => {
    if (usedNavPasswordRef.current) return;
    const pwd = (navPassword || '').trim();
    if (!pwd) return;
    usedNavPasswordRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
    setPassword(pwd);
  }, [location.pathname, navPassword, navigate]);

  useEffect(() => {
    if (password.trim() || navPassword.trim()) return;
    try {
      const saved = (sessionStorage.getItem(CM_PASSWORD_KEY) || '').trim();
      if (saved) {
        setPassword(saved);
      }
    } catch {
      // ignore
    }
    // 只在掛載時嘗試一次載入 sessionStorage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LOCAL_IMAGES_BY_ENTRY_ID = useMemo(
    () => ({ 'memory-2026-02-13-dp': [photoIluvu20260213] }),
    []
  );

  const imgUrl = (item) => (item && typeof item === 'object' ? item.url : item);

  const getEntryPhotos = useCallback(
    (entry) => {
      if (!entry) return [];
      const local = LOCAL_IMAGES_BY_ENTRY_ID[entry.id];
      if (Array.isArray(local) && local.length > 0) return local;

      const fromPath =
        Array.isArray(entry.path) && entry.path.length > 0
          ? entry.path.flatMap((p) => (Array.isArray(p.images) ? p.images.map(imgUrl) : []))
          : [];

      const fromRoot = Array.isArray(entry.images) && entry.images.length > 0
        ? entry.images.map(imgUrl)
        : entry.image
          ? [imgUrl(entry.image)]
          : [];

      const combined = [...fromPath, ...fromRoot];
      if (combined.length > 0) return combined;
      return [];
    },
    [LOCAL_IMAGES_BY_ENTRY_ID]
  );
  const STEP_PX = 120;
  const MIN_SCROLL_HEIGHT = 800;
  const CARD_WINDOW_RADIUS = 8;

  // 手機 Web：登入後鎖住 body/html 滾動，只讓內層 ScrollDriver 滾動，避免滾到整頁
  useEffect(() => {
    if (!isAuthed) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, [isAuthed]);

  const sortedContent = useMemo(() => {
    const list = decryptedContent || [];
    return [...list].sort((a, b) => {
      const dateA = new Date(a.timestamp || '').getTime() || 0;
      const dateB = new Date(b.timestamp || '').getTime() || 0;
      return dateB - dateA;
    });
  }, [decryptedContent]);

  const filteredContent = useMemo(() => {
    if (!selectedTag) return sortedContent;
    const needle = String(selectedTag).toLowerCase();
    const textIncludesTag = (text) =>
      typeof text === 'string' && text.toLowerCase().includes(needle);
    return sortedContent.filter((entry) => {
      const matchByTag =
        entry.tags?.includes(selectedTag) ||
        entry.path?.some((p) => p.tags?.includes(selectedTag));
      const matchByPathText =
        Array.isArray(entry.path) &&
        entry.path.some((p) => textIncludesTag(p?.note));
      const matchByText =
        textIncludesTag(entry.title) ||
        textIncludesTag(entry.summary) ||
        textIncludesTag(entry.details);
      return matchByTag || matchByPathText || matchByText;
    });
  }, [selectedTag, sortedContent]);

  useEffect(() => {
    const entry = filteredContent[activeIdx];
    if (entry?.id !== 'memory-2026-02-13-dp') {
      setVinylPlaying(false);
      setWalkSongCurrentTime(0);
      setFlownLyrics([]);
      prevLyricKeyRef.current = null;
      walkSongRef.current?.pause();
    }
  }, [activeIdx, filteredContent]);

  const currentLyric = useMemo(() => {
    let last = null;
    for (let i = 0; i < walkSongLyrics.length; i++) {
      if (walkSongLyrics[i].time <= walkSongCurrentTime) last = walkSongLyrics[i];
      else break;
    }
    return last;
  }, [walkSongCurrentTime]);

  useEffect(() => {
    if (!vinylPlaying || !currentLyric) return;
    const key = `${currentLyric.time}-${currentLyric.text}`;
    if (prevLyricKeyRef.current === key) return;
    prevLyricKeyRef.current = key;
    setFlownLyrics((prev) => {
      const next = [...prev.slice(-7), {
        id: key,
        text: currentLyric.text,
        x: 36 + Math.random() * 12,
        y: 38 + Math.random() * 12,
        rotation: -8 + Math.random() * 16,
      }];
      return next;
    });
    const t = setTimeout(() => {
      setFlownLyrics((prev) => prev.filter((i) => i.id !== key));
    }, 4200);
    return () => clearTimeout(t);
  }, [vinylPlaying, currentLyric]);

  useEffect(() => {
    if (!vinylPlaying) {
      setFlownLyrics([]);
      prevLyricKeyRef.current = null;
    }
  }, [vinylPlaying]);

  // 仿 GitHub：依日期統計記憶筆數、該日第一則摘要、以及該日在 filteredContent 的第一個 index（用於點擊滾動）
  // 另外標記：該日是否有照片、是否為唱片（vinyl）特別日期，並估算「資訊量」分數（文字長度 + path 數量加權）
  const {
    dateCountMap,
    dateToSummary,
    dateToFirstIdx,
    dateHasPhotos,
    dateHasVinyl,
    dateInfoScoreMap,
  } = useMemo(() => {
    const countMap = {};
    const summaryMap = {};
    const firstIdxMap = {};
    const hasPhotosMap = {};
    const hasVinylMap = {};
    const infoScoreMap = {};
    filteredContent.forEach((entry, idx) => {
      const ts = entry.timestamp;
      if (!ts) return;
      const dateKey = String(ts).slice(0, 10);
      countMap[dateKey] = (countMap[dateKey] || 0) + 1;
      if (summaryMap[dateKey] == null) {
        summaryMap[dateKey] = entry.summary || entry.title || '';
        firstIdxMap[dateKey] = idx;
      }
      const photos = getEntryPhotos(entry);
      if (Array.isArray(photos) && photos.length > 0) {
        hasPhotosMap[dateKey] = true;
      }
      if (entry.id === 'memory-2026-02-13-dp') {
        hasVinylMap[dateKey] = true;
      }
      const textLength =
        (entry.summary ? entry.summary.length : 0) +
        (entry.details ? entry.details.length : 0);
      const pathCount = Array.isArray(entry.path) ? entry.path.length : 0;
      const entryInfoScore = textLength + pathCount * 80;
      infoScoreMap[dateKey] = (infoScoreMap[dateKey] || 0) + entryInfoScore;
    });
    return {
      dateCountMap: countMap,
      dateToSummary: summaryMap,
      dateToFirstIdx: firstIdxMap,
      dateHasPhotos: hasPhotosMap,
      dateHasVinyl: hasVinylMap,
      dateInfoScoreMap: infoScoreMap,
    };
  }, [filteredContent, getEntryPhotos]);

  // 橫向：7 行（星期 月火水木金土日）× N 列（週）；每格對應一天。以「週」對齊：row 0 = 週日
  // baseDate = 涵蓋「最早記憶的那週的週日」與「至少 53 週前」之較早者，避免最早 entry 落在 grid 範圍外滑不到
  const { dateGraphGrid, monthLabels } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const defaultBase = new Date(today);
    defaultBase.setDate(today.getDate() - today.getDay() - 52 * 7);
    // 找出最早有資料的日期（含當週週日）作為候選 baseDate；取較早者保證涵蓋全部記憶
    let baseDate = defaultBase;
    const dateKeys = Object.keys(dateCountMap || {});
    if (dateKeys.length > 0) {
      let earliestTime = Infinity;
      dateKeys.forEach((k) => {
        const [y, m, d] = k.split('-').map(Number);
        if (!y || !m || !d) return;
        const dt = new Date(y, m - 1, d);
        if (dt.getTime() < earliestTime) earliestTime = dt.getTime();
      });
      if (Number.isFinite(earliestTime)) {
        const earliest = new Date(earliestTime);
        earliest.setHours(0, 0, 0, 0);
        // 對齊到當週的週日
        const earliestSunday = new Date(earliest);
        earliestSunday.setDate(earliest.getDate() - earliest.getDay());
        if (earliestSunday.getTime() < defaultBase.getTime()) {
          baseDate = earliestSunday;
        }
      }
    }
    // 動態算出需要幾週才能蓋到今天
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const totalWeeks = Math.max(
      53,
      Math.ceil((today.getTime() - baseDate.getTime()) / msPerWeek) + 1
    );
    const monthAbbrev = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const labels = [];
    const sortedInfoScores = Array.from(
      new Set(
        Object.values(dateInfoScoreMap || {}).filter(
          (score) => typeof score === 'number' && score > 0
        )
      )
    ).sort((a, b) => a - b);
    const gridByDay = []; // gridByDay[getDay 0=Sun..6=Sat][week 0..totalWeeks-1]
    for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
      const row = [];
      for (let week = 0; week < totalWeeks; week++) {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + week * 7 + dayOfWeek);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const count = dateCountMap[key] || 0;
        const infoScore = dateInfoScoreMap[key] || 0;
        let level = 0;
        if (infoScore > 0 && sortedInfoScores.length > 0) {
          const maxIndex = sortedInfoScores.length - 1;
          let pos = sortedInfoScores.findIndex((s) => s >= infoScore);
          if (pos === -1) pos = maxIndex;
          const ratio = maxIndex > 0 ? pos / maxIndex : 1;
          if (ratio >= 0.75) level = 4;
          else if (ratio >= 0.5) level = 3;
          else if (ratio >= 0.25) level = 2;
          else level = 1;
        }
        row.push({
          date: key,
          count,
          level,
          summary: dateToSummary[key],
          firstIdx: dateToFirstIdx[key],
          hasPhotos: !!dateHasPhotos[key],
          isVinyl: !!dateHasVinyl[key],
        });
      }
      gridByDay.push(row);
    }
    for (let week = 0; week < totalWeeks; week++) {
      let label = '';
      for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + week * 7 + dayOfWeek);
        if (d.getDate() === 1) {
          label = monthAbbrev[d.getMonth()];
          break;
        }
      }
      labels.push(label);
    }
    return { dateGraphGrid: gridByDay, monthLabels: labels };
  }, [dateCountMap, dateToSummary, dateToFirstIdx, dateHasPhotos, dateHasVinyl, dateInfoScoreMap]);

  const displayRowOrder = [0, 1, 2, 3, 4, 5, 6];

  const activeDateKey = useMemo(() => {
    const entry = filteredContent[activeIdx];
    if (!entry?.timestamp) return null;
    return String(entry.timestamp).slice(0, 10);
  }, [filteredContent, activeIdx]);

  const recomputeActive = useCallback(() => {
    const el = scrollDriverRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const n = filteredContent.length;
    if (n === 0) return;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const idx = n <= 1 ? 0 : (maxScroll > 0 ? Math.round((scrollTop / maxScroll) * (n - 1)) : 0);
    setActiveIdx(Math.min(Math.max(idx, 0), n - 1));
  }, [filteredContent.length]);

  useEffect(() => {
    if (!isAuthed) return;
    const el = scrollDriverRef.current;
    if (!el) return;

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          recomputeActive();
          ticking = false;
        });
        ticking = true;
      }
    };

    recomputeActive();
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [isAuthed, recomputeActive, filteredContent.length]);

  useEffect(() => {
    setActiveIdx(0);
    const el = scrollDriverRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filteredContent.length]);

  const scrollToCard = useCallback((idx, instant = false) => {
    const el = scrollDriverRef.current;
    if (!el) return;
    const n = filteredContent.length;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const targetTop = n <= 1 ? 0 : (idx / Math.max(1, n - 1)) * maxScroll;
    setActiveIdx(idx);
    el.scrollTo({ top: targetTop, behavior: instant ? 'auto' : 'smooth' });
  }, [filteredContent.length]);

  const scrollInnerHeight = useMemo(() => {
    const n = filteredContent.length;
    if (n <= 1) return 0;
    return Math.max((n - 1) * STEP_PX, MIN_SCROLL_HEIGHT);
  }, [filteredContent.length]);

  const handleTagClick = useCallback((tag) => {
    setSelectedTag((prev) => {
      const next = prev === tag ? null : tag;
      trackEvent(EVENTS.TAG_CLICK, { tag, action: next ? 'select' : 'deselect' });
      return next;
    });
  }, []);

  const getRotation = useCallback((idx) => {
    const base = Math.sin((idx + 1) * 1.11) * 3.2;
    const wobble = Math.cos((idx + 1) * 0.63) * 1.4;
    return parseFloat((base + wobble).toFixed(2));
  }, []);

  const getPhotoRotation = useCallback(
    (entryId, photoIndex, src) => {
      try {
        if (decryptedContent && entryId && src) {
          const entry = decryptedContent.find((e) => e && e.id === entryId);
          if (!entry) return 0;
          const allImages = [
            ...(Array.isArray(entry.images) ? entry.images : []),
            ...(Array.isArray(entry.path)
              ? entry.path.flatMap((p) => (Array.isArray(p.images) ? p.images : []))
              : []),
          ];
          const match = allImages.find(
            (item) => item && typeof item === 'object' && item.url === src
          );
          if (match) {
            const val = Number(match.rotation);
            if (!Number.isNaN(val)) return val;
          }
        }
      } catch {
        // fallback to 0
      }
      return 0;
    },
    [decryptedContent]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setChecking(true);
    const pwd = password.trim();
    try {
      const apiBase = USE_SERVER_AUTH ? charleyMemoryConfig.apiBaseUrl?.replace(/\/$/, '') : null;
      if (apiBase) {
        const recordsFromApi = await loginAndFetchRecords({
          apiBaseUrl: apiBase,
          password: pwd,
        });

        // 後端資料 + 本地加密 content 合併
        let merged = Array.isArray(recordsFromApi) ? [...recordsFromApi] : [];
        try {
          let blob = encryptedBlobRef.current;
          if (!blob) {
            const res = await fetch(ENCRYPTED_CONTENT_URL);
            if (res.ok) {
              blob = await res.arrayBuffer();
              encryptedBlobRef.current = blob;
            }
          }
          if (blob) {
            const localData = await decryptContent(blob, pwd);
            if (Array.isArray(localData) && localData.length) {
              const byId = new Map();
              merged.forEach((rec) => {
                if (rec && rec.id) byId.set(rec.id, true);
              });
              localData.forEach((rec) => {
                if (rec && rec.id && !byId.has(rec.id)) {
                  merged.push(rec);
                }
              });
            }
          }
        } catch (mergeErr) {
          // 合併本地內容失敗時不影響後端資料的顯示，只在 console 提示
          // eslint-disable-next-line no-console
          console.warn('Failed to merge local encrypted content', mergeErr);
        }

        trackEvent(EVENTS.AUTH_SUCCESS, { method: 'server', count: merged.length });
        setDecryptedContent(merged);
        setOpeningPhase('seal-fall');
      } else {
        let blob = encryptedBlobRef.current;
        if (!blob) {
          const res = await fetch(ENCRYPTED_CONTENT_URL);
          if (!res.ok) throw new Error('Could not load content');
          blob = await res.arrayBuffer();
          encryptedBlobRef.current = blob;
        }
        const data = await decryptContent(blob, pwd);
        trackEvent(EVENTS.AUTH_SUCCESS, { method: 'local', count: Array.isArray(data) ? data.length : 0 });
        setDecryptedContent(data);
        setOpeningPhase('seal-fall');
      }
      try {
        sessionStorage.setItem(CM_PASSWORD_KEY, pwd);
      } catch {
        // ignore
      }
    } catch (err) {
      if (err instanceof CharleyMemoryApiError) {
        if (err.code === CharleyMemoryApiErrorCode.INVALID_PASSWORD) {
          trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'invalid_password' });
          setError('密碼不正確');
        } else if (err.code === CharleyMemoryApiErrorCode.NETWORK) {
          trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'network_error' });
          setError('網路似乎有點問題，請稍後再試');
        } else {
          trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'server_error', code: err.code });
          setError('驗證或載入內容時出現問題，請稍後再試');
        }
      } else if (err.message?.includes('decrypt') || err.name === 'OperationError') {
        trackEvent(EVENTS.AUTH_FAIL, { method: 'local', reason: 'decrypt_error' });
        setError('密碼不正確');
      } else if (
        err?.name === 'SyntaxError' ||
        /Invalid content JSON|Invalid content format|Unexpected token/i.test(err?.message || '')
      ) {
        trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'content_format_error' });
        setError('內容檔格式錯誤（content.encrypted），請重新產生加密內容後再試');
      } else if (
        !window.isSecureContext ||
        window.location.protocol === 'http:' ||
        !window.crypto?.subtle ||
        /crypto\.subtle|secure context|secure context required/i.test(err?.message || '')
      ) {
        trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'insecure_context' });
        setError('此頁面需在 HTTPS 安全連線下使用，且需支援 Web 加密的瀏覽器。請確認以 https:// 開啟，或使用 Chrome、Firefox、Safari、Edge 的最新版本。');
      } else if (
        err?.name === 'TypeError' &&
        /failed to fetch|load failed|networkerror/i.test(err?.message || '')
      ) {
        trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'network_error' });
        setError('網路似乎有點問題，請稍後再試');
      } else {
        console.error('Auth/load failed', err);
        trackEvent(EVENTS.AUTH_FAIL, { method: USE_SERVER_AUTH ? 'server' : 'local', reason: 'unknown', message: err?.message });
        setError('驗證時出現問題，請確認使用 HTTPS 開啟網頁，或換個瀏覽器再試');
      }
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (decryptedContent) return;
    const pwd = password.trim();
    if (!pwd) return;
    if (pwd.length !== 16) return;
    const t = setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 150);
    return () => clearTimeout(t);
  }, [password, decryptedContent]);

  useEffect(() => {
    if (openingPhase === 'seal-fall') {
      const t = setTimeout(() => setOpeningPhase('envelope-open'), 600);
      return () => clearTimeout(t);
    }
    if (openingPhase === 'envelope-open') {
      const t = setTimeout(() => setOpeningPhase(null), 450);
      return () => clearTimeout(t);
    }
  }, [openingPhase]);

  const galleryPhotos = galleryEntry ? getEntryPhotos(galleryEntry) : [];
  const scrollGalleryToIndex = useCallback((index) => {
    const el = galleryScrollRef.current;
    if (!el) return;
    const target = el.children[index];
    if (!target) return;
    const left = target.offsetLeft - (el.offsetWidth - target.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, []);

  const galleryPrev = useCallback(() => {
    if (!galleryPhotos.length) return;
    setGalleryIndex((i) => {
      const nextIndex = i <= 0 ? galleryPhotos.length - 1 : i - 1;
      scrollGalleryToIndex(nextIndex);
      return nextIndex;
    });
  }, [galleryPhotos.length, scrollGalleryToIndex]);

  const galleryNext = useCallback(() => {
    if (!galleryPhotos.length) return;
    setGalleryIndex((i) => {
      const nextIndex = i >= galleryPhotos.length - 1 ? 0 : i + 1;
      scrollGalleryToIndex(nextIndex);
      return nextIndex;
    });
  }, [galleryPhotos.length, scrollGalleryToIndex]);
  const closeGallery = useCallback(() => {
    setGalleryEntry(null);
    setGalleryIndex(0);
  }, []);

  useEffect(() => {
    if (!galleryEntry || !galleryScrollRef.current) return;
    const el = galleryScrollRef.current;

    let frameId = null;

    const updateClosestIndex = () => {
      const children = Array.from(el.children || []);
      if (!children.length) return;

      const scrollLeft = el.scrollLeft;
      const maxScroll = el.scrollWidth - el.clientWidth;

      // 特別處理最左、最右：只要捲動接近邊界，就鎖在第一或最後一張
      if (scrollLeft <= 8) {
        if (galleryIndexRef.current !== 0) {
          setGalleryIndex(0);
        }
        return;
      }
      if (maxScroll > 0 && scrollLeft >= maxScroll - 8) {
        const lastIdx = children.length - 1;
        if (galleryIndexRef.current !== lastIdx) {
          setGalleryIndex(lastIdx);
        }
        return;
      }

      const center = scrollLeft + el.clientWidth / 2;
      let closestIdx = 0;
      let minDist = Infinity;
      children.forEach((child, idx) => {
        const rectLeft = child.offsetLeft;
        const rectRight = rectLeft + child.offsetWidth;
        const rectCenter = (rectLeft + rectRight) / 2;
        const dist = Math.abs(rectCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closestIdx = idx;
        }
      });
      if (closestIdx !== galleryIndexRef.current) {
        setGalleryIndex(closestIdx);
      }
    };

    const handleScroll = () => {
      if (frameId != null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateClosestIndex();
      });
    };

    // 初次打開時，先把目前 index 對齊到中間
    const initialIndex = galleryIndexRef.current || 0;
    scrollGalleryToIndex(initialIndex);
    updateClosestIndex();

    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [galleryEntry, scrollGalleryToIndex]);

  useEffect(() => {
    galleryIndexRef.current = galleryIndex;
  }, [galleryIndex]);

  useEffect(() => {
    if (!galleryEntry) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        closeGallery();
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        galleryPrev();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        galleryNext();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [galleryEntry, closeGallery, galleryPrev, galleryNext]);

  return (
    <PageContainer $fillViewport={isAuthed}>
      {galleryEntry && galleryPhotos.length > 0 && (
        <GalleryModal
          photos={galleryPhotos}
          galleryIndex={galleryIndex}
          setGalleryIndex={setGalleryIndex}
          galleryIndexRef={galleryIndexRef}
          scrollGalleryToIndex={scrollGalleryToIndex}
          galleryScrollRef={galleryScrollRef}
          onClose={closeGallery}
          entryId={galleryEntry.id}
          getPhotoRotation={getPhotoRotation}
        />
      )}
      <ContentWrapper $fillViewport={isAuthed}>
        {!isAuthed ? (
          <EnvelopeGate
            formRef={formRef}
            password={password}
            setPassword={setPassword}
            error={error}
            onSubmit={handleSubmit}
            openingPhase={openingPhase}
          />
        ) : (
          <>
            {viewMode === 'map' && (
              <CharleyMemoryMapView
                content={filteredContent}
                getEntryPhotos={getEntryPhotos}
                getPhotoRotation={getPhotoRotation}
                onBack={() => { trackEvent(EVENTS.VIEW_MODE, { from: 'map', to: 'content' }); setViewMode('content'); }}
                onTagClick={handleTagClick}
              />
            )}
            {viewMode === 'stats' && (
              <CharleyMemoryStatsView
                content={sortedContent}
                spots={spots}
                onBack={() => { trackEvent(EVENTS.VIEW_MODE, { from: 'stats', to: 'content' }); setViewMode('content'); }}
              />
            )}
            {viewMode === 'guestbook' && (
              <CharleyMemoryGuestbookView
                onBack={() => { trackEvent(EVENTS.VIEW_MODE, { from: 'guestbook', to: 'content' }); setViewMode('content'); }}
              />
            )}
            <AuthContentColumn>
              <HeaderRow>
              <Header>
                <SubtitleRow>
                  <CountBadge title={selectedTag ? `篩選中：#${selectedTag}` : '回憶總數'}>
                    🃏 {filteredContent.length}
                    {selectedTag && sortedContent.length !== filteredContent.length && (
                      <span style={{ opacity: 0.6, fontWeight: 400, fontSize: '0.8rem' }}>
                        /{sortedContent.length}
                      </span>
                    )}
                  </CountBadge>
                  <ViewBtnGroup>
                    <MapViewBtn
                      type="button"
                      onClick={() => { trackEvent(EVENTS.VIEW_MODE, { from: 'content', to: 'map' }); setViewMode('map'); }}
                      title="地圖檢視"
                      aria-label="地圖檢視"
                    >
                      <MapPinsStack aria-hidden>
                        <MapPinIcon viewBox="0 0 24 24"><path d={PIN_PATH} /></MapPinIcon>
                        <MapPinIcon viewBox="0 0 24 24"><path d={PIN_PATH} /></MapPinIcon>
                        <MapPinIcon viewBox="0 0 24 24"><path d={PIN_PATH} /></MapPinIcon>
                      </MapPinsStack>
                    </MapViewBtn>
                    <MapViewBtn
                      type="button"
                      onClick={() => { trackEvent(EVENTS.VIEW_MODE, { from: 'content', to: 'stats' }); setViewMode('stats'); }}
                      title="統計"
                      aria-label="統計"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="3" y="12" width="4" height="9" rx="1" />
                        <rect x="10" y="7" width="4" height="14" rx="1" />
                        <rect x="17" y="3" width="4" height="18" rx="1" />
                      </svg>
                    </MapViewBtn>
                    <MapViewBtn
                      type="button"
                      onClick={() => { trackEvent(EVENTS.VIEW_MODE, { from: 'content', to: 'guestbook' }); setViewMode('guestbook'); }}
                      title="留言板"
                      aria-label="留言板"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </MapViewBtn>
                    <MapViewBtn
                      type="button"
                      onClick={() => {
                        trackEvent(EVENTS.NAV, { to: 'todos' });
                        const pwd = password.trim();
                        navigate(`${charleyMemoryConfig.routePath}/todos`, {
                          state: pwd ? { cm_password: pwd } : {},
                        });
                      }}
                      title="待辦事項"
                      aria-label="待辦事項"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M9 11l2 2 4-4" />
                        <path d="M7 4h10a2 2 0 0 1 2 2v14l-3-2-4 2-4-2-3 2V6a2 2 0 0 1 2-2z" />
                      </svg>
                    </MapViewBtn>
                  </ViewBtnGroup>
                  {selectedTag && (
                    <Tag type="button" onClick={() => setSelectedTag(null)} aria-label="清除標籤篩選">
                      ✕ #{selectedTag}
                    </Tag>
                  )}
                </SubtitleRow>
              </Header>
              <DateGraph
                dateGraphGrid={dateGraphGrid}
                monthLabels={monthLabels}
                activeDateKey={activeDateKey}
                tooltip={tooltip}
                setTooltip={setTooltip}
                onCellClick={(idx) => {
                  setActiveIdx(idx);
                  scrollToCard(idx, true);
                }}
                displayRowOrder={displayRowOrder}
              />
              </HeaderRow>

              <EnvelopePocket ref={envelopeRef} aria-label="卡片區">
                <ScrollDriver ref={scrollDriverRef}>
                  <ScrollDriverInner $height={scrollInnerHeight} />
                </ScrollDriver>
                <CardsStack>
                  {filteredContent.length === 0 && selectedTag && (
                    <EmptyState>
                      <span style={{ fontSize: '1.6rem' }}>🔍</span>
                      <span>沒有含 <strong>#{selectedTag}</strong> 的回憶</span>
                      <button
                        type="button"
                        onClick={() => setSelectedTag(null)}
                        style={{
                          background: 'rgba(255,255,255,0.12)',
                          border: 'none',
                          borderRadius: 8,
                          color: 'rgba(233,236,241,0.8)',
                          padding: '6px 14px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          pointerEvents: 'auto',
                        }}
                      >
                        清除篩選
                      </button>
                    </EmptyState>
                  )}
                  {filteredContent.map((entry, idx) => {
                    if (Math.abs(idx - activeIdx) > CARD_WINDOW_RADIUS) {
                      return null;
                    }
                    return (
                      <MemoryCard
                        key={entry.id}
                        entry={entry}
                        idx={idx}
                        activeIdx={activeIdx}
                        totalCount={filteredContent.length}
                        angle={getRotation(idx)}
                        getEntryPhotos={getEntryPhotos}
                        getPhotoRotation={getPhotoRotation}
                        onOpenGallery={(entry, index) => {
                          trackEvent(EVENTS.GALLERY_OPEN, { entry_id: entry?.id, photo_index: index });
                          setGalleryEntry(entry);
                          setGalleryIndex(index);
                        }}
                        onTagClick={handleTagClick}
                        onCardClick={(clickedEntry, clickedIdx) => {
                          trackEvent(EVENTS.CARD_CLICK, {
                            entry_id: clickedEntry?.id,
                            index: clickedIdx,
                            timestamp: clickedEntry?.timestamp,
                            title: clickedEntry?.title,
                          });
                        }}
                        isVinylEntry={entry.id === 'memory-2026-02-13-dp'}
                        walkSongRef={walkSongRef}
                        vinylPlaying={vinylPlaying}
                        setVinylPlaying={setVinylPlaying}
                        onWalkSongTimeUpdate={(t) => setWalkSongCurrentTime(t)}
                      />
                    );
                  })}
                </CardsStack>
              </EnvelopePocket>
          </AuthContentColumn>
          </>
        )}

        {filteredContent[activeIdx]?.id === 'memory-2026-02-13-dp' && flownLyrics.length > 0 && (
          <LyricsOverlay items={flownLyrics} />
        )}

      </ContentWrapper>
      <VersionBadge>{CM_VERSION}</VersionBadge>
    </PageContainer>
  );
};

export default CharleyMemoryPage;
